/**
 * Monaco signature help for Control Designer ScopeScript editor.
 */

import {
    listScopeScriptMembers,
    parseScopeScriptMethodParameters,
} from './controlDesignerScopeScript.js';
import { CONTROL_SCOPE_SCRIPT_LANGUAGE_ID } from './controlDesignerScopeScriptLanguage.js';
import {
    listCommandMembers,
    listEmbeddedUserControlsInDesign,
    listNamedControlsInDesign,
} from './controlDesignerScopeScriptCompletion.js';
import { buildControlModelMemberSignature } from './controlDesignerControlModelMembers.js';
import { parseFunctionParameterNames } from './controlDesignerScopeScriptRuntimeMembers.js';

/**
 * @param {import('monaco-editor').editor.ITextModel} model
 * @param {import('monaco-editor').Position} position
 * @returns {string|null}
 */
export function getQualifiedCalleeBeforeParen(model, position) {
    const line = model.getLineContent(position.lineNumber);
    let col = position.column - 2;
    if (col < 0)
        return null;

    if (line[col] === '(') {
        while (col >= 0 && line[col] === '(')
            col--;
    }
    else {
        const before = line.substring(0, position.column - 1);
        const lastOpen = before.lastIndexOf('(');
        if (lastOpen < 0)
            return null;
        col = lastOpen - 1;
    }

    if (col < 0)
        return null;

    let fullMethod = '';
    while (col >= 0) {
        const ch = line[col];
        if (/[a-zA-Z0-9_$]/.test(ch))
            fullMethod = ch + fullMethod;
        else if (ch === '.')
            fullMethod = '.' + fullMethod;
        else
            break;
        col--;
    }

    return fullMethod || null;
}

/**
 * @param {string} line
 * @param {import('monaco-editor').Position} position
 * @returns {boolean}
 */
export function isCaretInsideCallArgumentList(line, position) {
    const cursorIdx = position.column - 2;
    if (cursorIdx < 0)
        return false;

    let openIdx;
    if (line[cursorIdx] === '(')
        openIdx = cursorIdx;
    else {
        const before = line.substring(0, position.column - 1);
        openIdx = before.lastIndexOf('(');
        if (openIdx < 0)
            return false;
    }

    let depth = 1;
    for (let i = openIdx + 1; i <= cursorIdx; i++) {
        const c = line[i];
        if (c === '(')
            depth++;
        else if (c === ')') {
            depth--;
            if (depth <= 0)
                return false;
        }
    }

    return depth > 0;
}

/**
 * @param {string} line
 * @param {import('monaco-editor').Position} position
 * @returns {number}
 */
export function getActiveParameterIndex(line, position) {
    const before = line.substring(0, position.column - 1);
    const lastOpen = before.lastIndexOf('(');
    if (lastOpen < 0)
        return 0;

    let depth = 0;
    let activeIndex = 0;

    for (let i = lastOpen + 1; i < before.length; i++) {
        const c = before[i];
        if (c === '(')
            depth++;
        else if (c === ')') {
            if (depth === 0)
                break;
            depth--;
        }
        else if (c === ',' && depth === 0)
            activeIndex++;
    }

    return activeIndex;
}

/**
 * @param {string} methodName
 * @param {string[]} parameterNames
 * @returns {{ label: string, parameters: { label: string }[] }}
 */
export function buildSignatureFromParameterNames(methodName, parameterNames) {
    const names = parameterNames || [];
    return {
        label: `${methodName}(${names.join(', ')})`,
        parameters: names.map((name) => ({ label: name })),
    };
}

function isScopeScriptFunctionMember(scopeScriptText, memberName) {
    return listScopeScriptMembers(scopeScriptText).some(
        (member) => member.name === memberName && member.kind === 'function',
    );
}

function isDesignControlName(designRoot, memberName) {
    return listNamedControlsInDesign(designRoot).some(
        (control) => control.name === memberName && control.kind === 'control',
    );
}

function isEmbeddedUserControlRefName(designRoot, memberName) {
    return listEmbeddedUserControlsInDesign(designRoot).some((ref) => ref.name === memberName);
}

function resolveScopeScriptMethodSignature(scopeScriptText, methodName) {
    if (!isScopeScriptFunctionMember(scopeScriptText, methodName))
        return null;

    const params = parseScopeScriptMethodParameters(scopeScriptText, methodName);
    if (params == null)
        return null;

    return buildSignatureFromParameterNames(methodName, params);
}

function resolveLibraryMethodSignature(alias, methodName, deps) {
    const members = deps.getLibraryMembersByAlias?.()?.[alias] || [];
    const member = members.find((item) => item.name === methodName);
    if (!member || member.kind !== 'function')
        return null;

    const scopeScriptText = deps.getLibraryScopeScriptByAlias?.(alias) || '';
    const params = parseScopeScriptMethodParameters(scopeScriptText, methodName);
    if (params == null)
        return null;

    return buildSignatureFromParameterNames(`${alias}.${methodName}`, params);
}

function resolveEmbeddedUserControlMethodSignature(controlName, methodName, deps) {
    const members = deps.getEmbeddedUserControlMembersByName?.()?.[controlName] || [];
    const member = members.find((item) => item.name === methodName);
    if (!member || member.kind !== 'function')
        return null;

    const scopeScriptText = deps.getEmbeddedUserControlScopeScriptByName?.(controlName) || '';
    const params = parseScopeScriptMethodParameters(scopeScriptText, methodName);
    if (params == null)
        return null;

    return buildSignatureFromParameterNames(`${controlName}.${methodName}`, params);
}

function resolveCommandMethodSignature(root, memberName, deps) {
    const commands = listCommandMembers(deps.getCommands?.() || []);
    const command = commands.find((item) => item.name === memberName);
    if (!command)
        return null;

    return buildSignatureFromParameterNames(`${root}.${memberName}`, ['input']);
}

/**
 * @param {string} qualifiedName
 * @param {{
 *   getDesignRoot: () => unknown,
 *   getScopeScriptText: () => string,
 *   getCommands?: () => unknown,
 *   getCommands?: () => unknown,
 *   getApiFunction?: (name: string) => unknown,
 *   getUtilFunction?: (name: string) => unknown,
 *   getLibraryMembersByAlias?: () => Record<string, { name: string, kind?: string }[]>,
 *   getLibraryScopeScriptByAlias?: (alias: string) => string,
 *   getEmbeddedUserControlMembersByName?: () => Record<string, { name: string, kind?: string }[]>,
 *   getEmbeddedUserControlScopeScriptByName?: (controlName: string) => string,
 *   getScopeIncludes?: () => { As?: string }[],
 * }} deps
 * @returns {{ label: string, parameters: { label: string }[] }|null}
 */
export function resolveCallableSignature(qualifiedName, deps) {
    if (!qualifiedName)
        return null;

    const parts = qualifiedName.split('.').filter(Boolean);
    if (!parts.length)
        return null;

    const designRoot = deps.getDesignRoot?.();

    if (parts.length === 1) {
        const methodName = parts[0];
        return resolveScopeScriptMethodSignature(deps.getScopeScriptText?.() || '', methodName);
    }

    if (parts.length === 3) {
        const [root, controlName, methodName] = parts;
        if (root === 'Control' || root === 'this') {
            if (!isEmbeddedUserControlRefName(designRoot, controlName))
                return null;

            const signature = resolveEmbeddedUserControlMethodSignature(controlName, methodName, deps);
            if (!signature)
                return null;

            return {
                label: `${root}.${controlName}.${methodName}(${signature.parameters.map((param) => param.label).join(', ')})`,
                parameters: signature.parameters,
            };
        }

        return null;
    }

    if (parts.length !== 2)
        return null;

    const [root, memberName] = parts;
    const scopeScriptText = deps.getScopeScriptText?.() || '';

    if (root === 'api') {
        const fn = deps.getApiFunction?.(memberName);
        if (typeof fn !== 'function')
            return null;
        return buildSignatureFromParameterNames(
            `api.${memberName}`,
            parseFunctionParameterNames(fn),
        );
    }

    if (root === 'util') {
        const fn = deps.getUtilFunction?.(memberName);
        if (typeof fn !== 'function')
            return null;
        return buildSignatureFromParameterNames(
            `util.${memberName}`,
            parseFunctionParameterNames(fn),
        );
    }

    if (root === 'Control' || root === 'this') {
        if (isDesignControlName(designRoot, memberName))
            return null;

        const signature = resolveScopeScriptMethodSignature(scopeScriptText, memberName);
        if (signature) {
            return {
                label: `${root}.${memberName}(${signature.parameters.map((param) => param.label).join(', ')})`,
                parameters: signature.parameters,
            };
        }

        return resolveCommandMethodSignature(root, memberName, deps);
    }

    if (root === 'controlModel')
        return buildControlModelMemberSignature(memberName);

    const libraryMembers = deps.getLibraryMembersByAlias?.() || {};
    if (Object.prototype.hasOwnProperty.call(libraryMembers, root))
        return resolveLibraryMethodSignature(root, memberName, deps);

    const includes = deps.getScopeIncludes?.() || [];
    if (includes.some((inc) => String(inc?.As || '').trim() === root))
        return resolveLibraryMethodSignature(root, memberName, deps);

    return null;
}

/**
 * @param {{
 *   monaco: import('monaco-editor'),
 *   model: import('monaco-editor').editor.ITextModel,
 *   getDesignRoot: () => unknown,
 *   getScopeScriptText: () => string,
 *   getCommands?: () => unknown,
 *   getApiFunction?: (name: string) => unknown,
 *   getUtilFunction?: (name: string) => unknown,
 *   getLibraryMembersByAlias?: () => Record<string, { name: string, kind?: string }[]>,
 *   getLibraryScopeScriptByAlias?: (alias: string) => string,
 *   getEmbeddedUserControlMembersByName?: () => Record<string, { name: string, kind?: string }[]>,
 *   getEmbeddedUserControlScopeScriptByName?: (controlName: string) => string,
 *   getScopeIncludes?: () => { As?: string }[],
 * }} options
 * @returns {{ dispose: () => void }}
 */
export function createScopeScriptSignatureHelpProvider(options) {
    const {
        monaco,
        model,
        getDesignRoot,
        getScopeScriptText,
        getCommands,
        getApiFunction,
        getUtilFunction,
        getLibraryMembersByAlias,
        getLibraryScopeScriptByAlias,
        getEmbeddedUserControlMembersByName,
        getEmbeddedUserControlScopeScriptByName,
        getScopeIncludes,
    } = options;

    const deps = {
        getDesignRoot,
        getScopeScriptText,
        getCommands,
        getApiFunction,
        getUtilFunction,
        getLibraryMembersByAlias,
        getLibraryScopeScriptByAlias,
        getEmbeddedUserControlMembersByName,
        getEmbeddedUserControlScopeScriptByName,
        getScopeIncludes,
    };

    const provider = monaco.languages.registerSignatureHelpProvider(CONTROL_SCOPE_SCRIPT_LANGUAGE_ID, {
        signatureHelpTriggerCharacters: ['('],
        signatureHelpRetriggerCharacters: [','],
        provideSignatureHelp(editorModel, position, _token, context) {
            if (editorModel !== model)
                return null;

            const line = editorModel.getLineContent(position.lineNumber);
            const before = line.substring(0, position.column - 1);
            if (!before.trim().endsWith('(') && !context.isRetrigger)
                return null;

            if (!isCaretInsideCallArgumentList(line, position))
                return null;

            const qualifiedName = getQualifiedCalleeBeforeParen(editorModel, position);
            const signature = resolveCallableSignature(qualifiedName, deps);
            if (!signature)
                return null;

            const activeParameter = Math.min(
                getActiveParameterIndex(line, position),
                Math.max(0, signature.parameters.length - 1),
            );

            return {
                value: {
                    signatures: [signature],
                    activeSignature: 0,
                    activeParameter,
                },
                dispose: () => {},
            };
        },
    });

    return provider;
}
